import React, { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";

export const PANEL_CALENDAR_ID = "settlement-panel-calendar";

/** Common report panel with keyboard navigation and return focus. */
export default function SidePanel({ title, children = null, close }) {
    const element = useRef(null);
    const closeRef = useRef(close);
    useLayoutEffect(() => {
        closeRef.current = close;
    }, [close]);
    useEffect(() => {
        const dialog = element.current;
        const previousFocus = document.activeElement;
        dialog.querySelector("button")?.focus();
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                const calendar = dialog.querySelector(`#${PANEL_CALENDAR_ID} .react-datepicker-popper`);
                if (
                    calendar &&
                    (calendar.contains(event.target) ||
                        (dialog.contains(event.target) && event.target.closest(".react-datepicker-wrapper")))
                ) {
                    return;
                }
                event.stopPropagation();
                closeRef.current();
            }
            if (event.key !== "Tab") {
                return;
            }
            const controls = [
                ...dialog.querySelectorAll(
                    "button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex='0']",
                ),
            ].filter((control) => control.offsetParent !== null && control.tabIndex >= 0);
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first?.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown, true);
        return () => {
            document.removeEventListener("keydown", handleKeyDown, true);
            if (previousFocus?.isConnected) {
                previousFocus.focus();
            }
        };
    }, []);
    return createPortal(
        <div className="settlements__panel-layer">
            <button
                type="button"
                className="settlements__panel-backdrop border-0 p-0"
                tabIndex={-1}
                aria-label="Закрыть панель"
                onClick={(event) => {
                    if (event.target === event.currentTarget) {
                        close();
                    }
                }}
            />
            <section
                ref={element}
                className="settlements__panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="settlement-panel-title"
            >
                <div className="settlements__panel-header d-flex justify-content-between align-items-center p-3 border-bottom">
                    <h5 className="mb-0" id="settlement-panel-title">
                        {title}
                    </h5>
                    <button
                        type="button"
                        className="btn btn-sm btn-light settlements__panel-close"
                        aria-label="Закрыть панель"
                        onClick={close}
                    >
                        ×
                    </button>
                </div>
                <div className="settlements__panel-body p-3">{children}</div>
                <div id={PANEL_CALENDAR_ID} className="settlements__calendar-layer" />
            </section>
        </div>,
        document.body,
    );
}

export function PanelStatus({ loading, error, retry }) {
    return (
        <>
            {loading && (
                <div className="settlements__loading small mb-3" role="status">
                    Обновляем данные…
                </div>
            )}
            {error && (
                <div className="alert alert-danger" role="alert">
                    {error}
                    <button type="button" className="btn btn-sm btn-secondary mt-2 d-block" onClick={retry}>
                        Повторить
                    </button>
                </div>
            )}
        </>
    );
}
